/**
 * useRoutePreload - Hook for prefetching route components
 *
 * Preloads route components when user hovers over navigation links
 * or when likely to navigate to them.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Preload a route component
 */
function preloadRoute(routePath: string) {
  // Create a link element with rel="prefetch" to hint the browser
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.href = routePath;
  link.as = "document";
  document.head.appendChild(link);
}

/**
 * Hook to preload likely next routes based on current location
 */
export function useRoutePreload() {
  const location = useLocation();

  useEffect(() => {
    // Preload likely next routes based on current page
    const likelyRoutes: string[] = [];

    switch (location.pathname) {
      case "/":
        // From homepage, likely to visit: ecosystem, tokenomics, about-us
        likelyRoutes.push("/ecosystem", "/tokenomics", "/about-us");
        break;
      case "/ecosystem":
        // From ecosystem, likely to visit: tokenomics, traction
        likelyRoutes.push("/tokenomics", "/traction");
        break;
      case "/tokenomics":
        // From tokenomics, likely to visit: ecosystem, traction
        likelyRoutes.push("/ecosystem", "/traction");
        break;
      case "/about-us":
        // From about-us, likely to visit: our-team, ecosystem
        likelyRoutes.push("/our-team", "/ecosystem");
        break;
      default:
        // For other pages, preload homepage and ecosystem (most common)
        likelyRoutes.push("/", "/ecosystem");
    }

    // Preload likely routes after a short delay
    const timeoutId = setTimeout(() => {
      likelyRoutes.forEach((route) => {
        preloadRoute(route);
      });
    }, 2000); // Wait 2 seconds after page load

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
}

/**
 * Preload route on link hover
 * Use this in navigation components
 */
export function usePreloadOnHover(href: string) {
  const handleMouseEnter = () => {
    preloadRoute(href);
  };

  return { onMouseEnter: handleMouseEnter };
}
